import { createContext, useState, useEffect, useCallback, useContext, ReactNode } from 'react'
import { api } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import type { UsageInfo } from '@/services/types'

interface SubscriptionContextType {
  usage: UsageInfo | null
  canGenerate: boolean
  isLoading: boolean
  refreshUsage: () => Promise<UsageInfo | null>
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null)

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [usage, setUsage] = useState<UsageInfo | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const refreshUsage = useCallback(async (): Promise<UsageInfo | null> => {
    try {
      const response = await api.getUsage()
      setUsage(response.usage)
      return response.usage
    } catch {
      // Silently fail — usage stays null, UI degrades gracefully
      return null
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      setIsLoading(true)
      refreshUsage().finally(() => setIsLoading(false))
    } else {
      setUsage(null)
    }
  }, [isAuthenticated, refreshUsage])

  const remaining = usage
    ? (typeof usage.generations_remaining === 'number' ? usage.generations_remaining : usage.generations_limit - usage.generations_used)
    : 0
  const canGenerate = usage ? usage.is_unlimited || remaining > 0 : true

  return (
    <SubscriptionContext.Provider value={{ usage, canGenerate, isLoading, refreshUsage }}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscription() {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider')
  }
  return context
}
